import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { useMemberstack } from "@/lib/memberstack";

const PRICE_MONTHLY = process.env.REACT_APP_MEMBERSTACK_PRICE_MONTHLY;
const PRICE_ANNUAL = process.env.REACT_APP_MEMBERSTACK_PRICE_ANNUAL;

const PLANS = [
  {
    id: "monthly",
    label: "Monthly",
    headline: "£49",
    cadence: "/month",
    sub: "Cancel any time. Inc. VAT.",
    badge: null,
    accent: "#4aa0a4",
    priceIdEnv: PRICE_MONTHLY,
  },
  {
    id: "annual",
    label: "Annual",
    headline: "£490",
    cadence: "/year",
    sub: "12 months for the price of 10. Inc. VAT.",
    badge: "Save £98",
    accent: "#f4b847",
    priceIdEnv: PRICE_ANNUAL,
  },
];

export default function Join() {
  const navigate = useNavigate();
  const { memberstack, isAuthenticated, loading: authLoading, refreshMember } = useMemberstack();
  const [selected, setSelected] = useState("annual");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [whatBuilding, setWhatBuilding] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // Preserve `?cancelled=1` so the dashboard can show a friendly "no payment taken" toast
      const params = new URLSearchParams(window.location.search);
      const target = params.get("cancelled") === "1" ? "/dashboard?cancelled=1" : "/dashboard";
      navigate(target, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const selectedPlan = PLANS.find((p) => p.id === selected);
  const priceIdConfigured =
    selectedPlan?.priceIdEnv &&
    !selectedPlan.priceIdEnv.startsWith("REPLACE_WITH");

  const canSubmit =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    email.trim().length >= 5 &&
    password.length >= 8 &&
    !submitting &&
    !!memberstack &&
    priceIdConfigured;

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);

    try {
      // 1. Create the member account (no custom fields yet — some SDK versions reject them here)
      const signupRes = await memberstack.signupMemberEmailPassword({
        email: email.trim(),
        password,
      });

      // Memberstack DOM responses sometimes use `errors` (array), sometimes `error`.
      const signupErrors = signupRes?.errors || (signupRes?.error ? [signupRes.error] : null);
      if (signupErrors?.length) {
        throw new Error(signupErrors[0]?.message || "Sign-up failed");
      }

      // 2. Best-effort: write the first + last name + "what building" into the member profile.
      // Don't block checkout if this fails — we can always pick the name up later.
      try {
        const customFields = {
          "first-name": firstName.trim(),
          "last-name": lastName.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        };
        if (whatBuilding.trim()) {
          customFields["what-building"] = whatBuilding.trim();
          customFields.whatBuilding = whatBuilding.trim();
        }
        await memberstack.updateMember({ customFields });
      } catch (updateErr) {
        // Non-fatal — but keep visible in prod console for member-support diagnostics.
        // eslint-disable-next-line no-console
        console.warn("[join] customFields update failed (non-fatal)", updateErr);
      }

      // 3. Launch Stripe checkout. Keep call shape minimal first; some SDK versions
      // reject extra keys. Memberstack will return to its configured post-checkout redirect.
      const checkoutRes = await memberstack.purchasePlansWithCheckout({
        priceId: selectedPlan.priceIdEnv,
        cancelUrl: `${window.location.origin}/join?cancelled=1`,
        successUrl: `${window.location.origin}/dashboard?welcome=1`,
      });

      // If we reach here without a redirect, surface the result
      const checkoutErrors =
        checkoutRes?.errors || (checkoutRes?.error ? [checkoutRes.error] : null);
      if (checkoutErrors?.length) {
        throw new Error(checkoutErrors[0]?.message || "Checkout failed");
      }

      // Some SDK versions return a URL to redirect to manually
      const redirectUrl = checkoutRes?.data?.url || checkoutRes?.url;
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      // No redirect, no errors — refresh member state in case checkout completed inline
      await refreshMember();
    } catch (err) {
      // Kept visible in production — first thing to ask a stuck member to send.
      // eslint-disable-next-line no-console
      console.error("[join] signup/checkout failed", err);
      const msg = err?.message || "Couldn't complete signup. Please try again.";
      toast.error("Sign-up issue", { description: msg });
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="join-page">
      <SiteHeader onLogoClick={() => window.location.assign("https://www.wearetheweave.co.uk")} />

      <main className="max-w-3xl mx-auto px-6 pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#3c8386" }}
            data-testid="join-eyebrow"
          >
            The Weave's 1% Club
          </p>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-light text-slate-800 tracking-tight leading-[1.05]"
            data-testid="join-headline"
          >
            A small, brave room
            <br />
            for people building something kinder.
          </h1>
          <p className="mt-6 text-lg md:text-xl font-light text-slate-600 leading-relaxed max-w-2xl">
            A founder-first membership delivering value from day one. Four
            rooms — already open and working for you.
          </p>
        </motion.div>

        <div
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="join-pillars"
        >
          {[
            {
              key: "facilitation",
              letter: "F",
              label: "Facilitation",
              color: "#4aa0a4",
              line: "Introductions and referrals — start with a 15-min welcome call.",
            },
            {
              key: "education",
              letter: "E",
              label: "Education",
              color: "#f4b847",
              line: "Build focus and strategy using AWAKE and the Funding Hub.",
            },
            {
              key: "entertainment",
              letter: "E",
              label: "Entertainment",
              color: "#3c8386",
              line: "Daily strength from our community — pull a thread, play a game.",
            },
            {
              key: "development",
              letter: "D",
              label: "Development",
              color: "#e93e36",
              line: "Funding clinics and a podcast platform to amplify your story.",
            },
          ].map((p) => (
            <div
              key={p.key}
              className="weave-join-pillar"
              style={{
                borderColor: `${p.color}33`,
                background: `${p.color}08`,
              }}
              data-testid={`join-pillar-${p.key}`}
            >
              <span
                className="weave-join-pillar-letter"
                style={{ background: p.color }}
                aria-hidden="true"
              >
                {p.letter}
              </span>
              <div className="min-w-0">
                <p
                  className="text-xs uppercase tracking-[0.18em] font-semibold leading-none mb-1"
                  style={{ color: p.color }}
                >
                  {p.label}
                </p>
                <p className="text-sm text-slate-600 leading-snug">{p.line}</p>
              </div>
            </div>
          ))}
        </div>

        <p
          className="mt-5 text-sm text-slate-500 text-center md:text-left"
          data-testid="join-cancel-line"
        >
          Cancel anytime — easy in, easy out.
        </p>

        <div className="weave-divider" />

        <div className="grid md:grid-cols-2 gap-4" data-testid="plan-cards">
          {PLANS.map((p) => {
            const isSelected = selected === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p.id)}
                data-testid={`plan-${p.id}`}
                className="weave-plan-card text-left"
                style={{
                  borderColor: isSelected ? p.accent : "#e2e8f0",
                  background: isSelected ? `${p.accent}0a` : "#ffffff",
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span
                    className="text-[11px] uppercase tracking-[0.18em] font-medium"
                    style={{ color: isSelected ? p.accent : "#94a3b8" }}
                  >
                    {p.label}
                  </span>
                  {p.badge && (
                    <span
                      className="text-[10px] uppercase tracking-[0.16em] font-medium px-2 py-0.5 rounded-full"
                      style={{
                        background: `${p.accent}22`,
                        color: "#8a6612",
                      }}
                    >
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-light text-slate-800 tracking-tight">
                    {p.headline}
                  </span>
                  <span className="text-base text-slate-500">{p.cadence}</span>
                </div>
                <p className="text-sm text-slate-500">{p.sub}</p>
                <span className="absolute top-4 right-4">
                  {isSelected && (
                    <Check size={18} style={{ color: p.accent }} />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-light text-slate-800 mb-6">
            Your details
          </h2>

          <form onSubmit={handleJoin} className="space-y-5" data-testid="join-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2" htmlFor="join-first-name">
                  First name
                </label>
                <input
                  id="join-first-name"
                  type="text"
                  className="weave-input"
                  placeholder="Anna"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  disabled={submitting}
                  maxLength={40}
                  data-testid="join-first-name"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-500 mb-2" htmlFor="join-last-name">
                  Last name
                </label>
                <input
                  id="join-last-name"
                  type="text"
                  className="weave-input"
                  placeholder="Bell"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  disabled={submitting}
                  maxLength={60}
                  data-testid="join-last-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-500 mb-2" htmlFor="join-email">
                Email
              </label>
              <input
                id="join-email"
                type="email"
                className="weave-input"
                placeholder="you@yourwork.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={submitting}
                data-testid="join-email"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-500 mb-2" htmlFor="join-password">
                Set a password
              </label>
              <div className="relative">
                <input
                  id="join-password"
                  type={showPassword ? "text" : "password"}
                  className="weave-input pr-12"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={submitting}
                  minLength={8}
                  data-testid="join-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="weave-pw-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  disabled={submitting}
                  data-testid="join-password-toggle"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400 inline-flex items-center gap-1">
                <Lock size={11} />
                Used to log in to the member dashboard.
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-500 mb-2" htmlFor="join-what-building">
                What are you building?{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="join-what-building"
                className="weave-input"
                rows={2}
                placeholder="A sentence or two — helps us welcome you well."
                value={whatBuilding}
                onChange={(e) => setWhatBuilding(e.target.value)}
                disabled={submitting}
                maxLength={280}
                data-testid="join-what-building"
              />
              <p className="mt-2 text-xs text-slate-400">
                {whatBuilding.length > 0
                  ? `${280 - whatBuilding.length} characters left`
                  : "Skip if you'd rather tell us in person."}
              </p>
            </div>

            {!priceIdConfigured && (
              <div className="weave-card-soft" data-testid="join-price-missing">
                <p className="text-sm text-slate-700">
                  This plan isn't fully connected yet — the Stripe Price ID is
                  missing from the app config.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Add{" "}
                  <code>
                    REACT_APP_MEMBERSTACK_PRICE_{selected.toUpperCase()}
                  </code>{" "}
                  to <code>/app/frontend/.env</code> and restart, then try
                  again.
                </p>
              </div>
            )}

            <button
              type="submit"
              className="weave-btn-primary w-full"
              disabled={!canSubmit}
              data-testid="join-submit"
            >
              {submitting ? (
                <>
                  <span className="weave-spinner" />
                  Preparing checkout…
                </>
              ) : (
                <>
                  Continue to Stripe — {selectedPlan?.headline}
                  {selectedPlan?.cadence}
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-xs text-slate-400 text-center">
              You'll complete payment securely on Stripe. After checkout,
              you'll land on your member dashboard.
            </p>

            <p className="text-xs text-slate-400 text-center" data-testid="join-legal-line">
              By continuing, you agree to The Weave's{" "}
              <a
                href="https://www.wearetheweave.co.uk/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-slate-300 underline-offset-2 hover:text-[#3c8386] hover:decoration-[#3c8386] transition-colors"
                data-testid="join-terms-link"
              >
                terms of service
              </a>{" "}
              and{" "}
              <a
                href="https://www.wearetheweave.co.uk/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-slate-300 underline-offset-2 hover:text-[#3c8386] hover:decoration-[#3c8386] transition-colors"
                data-testid="join-privacy-link"
              >
                privacy policy
              </a>
              .
            </p>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
            Already a member?{" "}
            <Link
              to="/login"
              className="font-medium text-slate-700 hover:text-[#3c8386] transition-colors"
              data-testid="join-login-link"
            >
              Log in
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
